import React, { useMemo } from 'react'
import {
    Box,
    CircularProgress,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import {
    InfoOutlined,
    Search as SearchIcon,
} from '@mui/icons-material'

import { Sparkline } from './Sparkline'
import { BoatLocation } from './ServiceTable'
import { STATUS_COLORS } from './FleetStatusBar'

// Active statuses pulse; Shutdown / Offline are static dots.
const ACTIVE_STATUSES = new Set(['Online', 'Running', 'Awake'])

// Tiny status beacon (map colors). Active boats pulse in their OWN color so the
// beacon reads the same as its map marker (red pulse for a red boat, etc.).
// Keyframes are named per status: emotion dedupes @keyframes by name, so a
// shared name would collapse all colors to whichever rendered first.
const StatusBeacon: React.FC<{ status: string }> = ({ status }) => {
    const color = STATUS_COLORS[status] || '#9AA0A6'
    const active = ACTIVE_STATUSES.has(status)
    const anim = `beaconPulse_${status.replace(/[^A-Za-z0-9]/g, '')}`
    return (
        <Tooltip title={status} arrow placement="top">
            <Box
                sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: color,
                    flexShrink: 0,
                    ...(active && {
                        animation: `${anim} 1.6s ease-out infinite`,
                        [`@keyframes ${anim}`]: {
                            '0%': { boxShadow: `0 0 0 0 ${color}E6` },
                            '70%': { boxShadow: `0 0 0 8px ${color}00` },
                            '100%': { boxShadow: `0 0 0 0 ${color}00` },
                        },
                    }),
                }}
            />
        </Tooltip>
    )
}

export interface DealerPerf {
    dealer: string
    activeBoats: number
    totalHours: number
    avgHours: number
    totalHoursDeltaPct?: number | null
    avgHoursDeltaPct?: number | null
    /** Daily engine-hours series for the boats-mode sparkline. */
    series?: number[]
    /** Per-day alarm / DTC counts aligned to `series` (boats-mode markers). */
    alarms?: number[]
    dtcs?: number[]
    /** "MY model" secondary label (boats mode). */
    model?: string
    /** Device serial — correlates a boat row to its map point (boats mode). */
    id?: string
    /** Fleet status label (boats mode) — drives the status beacon color/pulse. */
    status?: string
    /** Offline reverse-geocoded location (boats mode, resolved server-side). */
    city?: string
    state?: string
    country?: string
    /** Group mode: row drills into a child level (shows a ▸ and is clickable). */
    drillable?: boolean
}

/**
 * Horizontal bar scaled to the column's #1 performer. The value sits at the
 * left and the period-over-period delta (green ▲ / red ▼, like the KPI cards)
 * sits at the right — both layered inside the bar.
 */
const Bar: React.FC<{
    value: number
    max: number
    color: string
    deltaPct?: number | null
}> = ({ value, max, color, deltaPct }) => {
    const hasDelta = deltaPct != null && deltaPct !== 0
    const up = (deltaPct ?? 0) > 0
    return (
        <Box
            sx={{
                position: 'relative',
                height: 20,
                borderRadius: 0.5,
                backgroundColor: 'rgba(255,255,255,0.05)',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    width: `${max > 0 ? (value / max) * 100 : 0}%`,
                    backgroundColor: color,
                    opacity: 0.35,
                }}
            />
            <Box
                sx={{
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 0.75,
                    gap: 0.5,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, lineHeight: 1 }}
                >
                    {value.toLocaleString()}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 700,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        color: hasDelta
                            ? up
                                ? '#8BC441'
                                : '#E00000'
                            : 'text.disabled',
                    }}
                >
                    {hasDelta ? `${up ? '▲' : '▼'} ${Math.abs(deltaPct!)}%` : '—'}
                </Typography>
            </Box>
        </Box>
    )
}

interface Props {
    data: DealerPerf[]
    /** Range label for the caption, e.g. "14d" or a date range. */
    rangeLabel?: string
    /** Container title (e.g. "Top Performers"). */
    title?: string
    /** First-column header + empty-state noun (e.g. "Dealer", "Country"). */
    entityLabel?: string
    /** Optional tab strip rendered under the header. */
    tabs?: React.ReactNode
    /** Optional node rendered between the tabs and the table (e.g. a breadcrumb). */
    breadcrumb?: React.ReactNode
    /** Render bare (no card chrome / title / tabs) — a shell owns those. */
    embedded?: boolean
    /** Boats mode: drop the sparkline "Performance" column (light boat rows). */
    hideSparkline?: boolean
    /**
     * 'group' (default): Active + Total Hrs + Avg Hrs/Boat (dealers/countries).
     * 'boats': per-boat — drops Active & Avg, last column is an hours sparkline.
     */
    mode?: 'group' | 'boats'
    /** ISO date axis aligned to each row's `series` (boats-mode sparkline hover). */
    days?: string[]
    /** Row click (boats mode): e.g. focus the boat on the fleet map. */
    onRowClick?: (row: DealerPerf) => void
    /** Boats mode: render a fleet-wide search box. */
    searchable?: boolean
    searchValue?: string
    onSearchChange?: (v: string) => void
    searching?: boolean
}

export const DealerPerformanceTable: React.FC<Props> = ({
    data,
    rangeLabel,
    title = 'Top Performers',
    entityLabel = 'Dealer',
    tabs,
    breadcrumb,
    embedded,
    hideSparkline,
    mode = 'group',
    days,
    onRowClick,
    searchable,
    searchValue,
    onSearchChange,
    searching,
}) => {
    const isBoats = mode === 'boats'
    // Whether the last column (sparkline in boats mode, Avg Hrs in group mode)
    // is shown. Light boat rows (dealer drill / "All") drop the sparkline.
    const showLastCol = !(isBoats && hideSparkline)
    const maxTotal = useMemo(
        () => Math.max(1, ...data.map((d) => d.totalHours)),
        [data]
    )
    const maxAvg = useMemo(
        () => Math.max(1, ...data.map((d) => d.avgHours)),
        [data]
    )

    return (
        <Paper
            elevation={0}
            sx={{
                overflow: 'hidden',
                height: '100%',
                ...(embedded && {
                    bgcolor: 'transparent',
                    backgroundImage: 'none',
                }),
            }}
        >
            {!embedded && (
            <Box
                sx={{
                    px: 2.5,
                    pt: 2,
                    pb: tabs ? 0.5 : 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Tooltip
                    title={`by total engine hours · Δ vs prior period${
                        rangeLabel ? ` · ${rangeLabel}` : ''
                    }`}
                    arrow
                    placement="top"
                >
                    <InfoOutlined
                        sx={{
                            fontSize: 15,
                            color: 'text.disabled',
                            cursor: 'help',
                            '&:hover': { color: 'text.secondary' },
                        }}
                    />
                </Tooltip>
            </Box>
            )}
            {!embedded && tabs}
            {breadcrumb}
            {searchable && !embedded && (
                <Box sx={{ px: 2.5, pt: 1, pb: 0.5 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search all boats by HIN or model…"
                        value={searchValue || ''}
                        onChange={(e) => onSearchChange?.(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            fontSize: 18,
                                            color: 'text.disabled',
                                        }}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment: searching ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={14} />
                                </InputAdornment>
                            ) : undefined,
                        }}
                    />
                </Box>
            )}
            <Box sx={{ overflowX: 'auto' }}>
                <Table
                    size="small"
                    sx={{
                        minWidth: 540,
                        // More breathing room between the card border and the
                        // row text (first/last columns).
                        '& td:first-of-type, & th:first-of-type': { pl: 2.5 },
                        '& td:last-of-type, & th:last-of-type': { pr: 2.5 },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>{entityLabel}</TableCell>
                            {isBoats && <TableCell>Location</TableCell>}
                            {!isBoats && (
                                <TableCell align="right">Active</TableCell>
                            )}
                            <TableCell sx={{ width: '34%' }}>
                                Total Hrs
                            </TableCell>
                            {showLastCol && (
                            <TableCell sx={{ width: '28%' }}>
                                {isBoats ? (
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        Performance
                                        <Tooltip
                                            arrow
                                            placement="top"
                                            title={
                                                <span>
                                                    Daily engine hours over the
                                                    period. Markers flag fault
                                                    days —{' '}
                                                    <b
                                                        style={{
                                                            color: '#F59E0B',
                                                        }}
                                                    >
                                                        orange = alarm
                                                    </b>
                                                    ,{' '}
                                                    <b
                                                        style={{
                                                            color: '#E00000',
                                                        }}
                                                    >
                                                        red = DTC
                                                    </b>
                                                    , split = both. Hover any
                                                    point for the date, hours,
                                                    and fault counts.
                                                </span>
                                            }
                                        >
                                            <InfoOutlined
                                                sx={{
                                                    fontSize: 14,
                                                    color: 'text.disabled',
                                                    cursor: 'help',
                                                    '&:hover': {
                                                        color: 'text.secondary',
                                                    },
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                ) : (
                                    'Avg Hrs/Boat'
                                )}
                            </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    sx={{ color: 'text.secondary' }}
                                >
                                    No {entityLabel.toLowerCase()} activity in
                                    this period.
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((d, i) => {
                                // Clickable in boats mode (focus on map) or when
                                // a group row can drill into a child level.
                                const clickable =
                                    !!onRowClick && (isBoats || !!d.drillable)
                                return (
                                <TableRow
                                    key={d.dealer}
                                    hover
                                    onClick={
                                        clickable
                                            ? () => onRowClick!(d)
                                            : undefined
                                    }
                                    sx={{
                                        cursor: clickable
                                            ? 'pointer'
                                            : 'default',
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            maxWidth: 200,
                                            // Boats: drop the shared left padding
                                            // so the beacon sits centered in its
                                            // own gutter (at the midpoint between
                                            // the card edge and the text).
                                            ...(isBoats && d.status
                                                ? { pl: '0 !important' }
                                                : {}),
                                        }}
                                        title={d.dealer}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {isBoats && d.status && (
                                                <Box
                                                    sx={{
                                                        width: 36,
                                                        flexShrink: 0,
                                                        display: 'flex',
                                                        justifyContent:
                                                            'center',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <StatusBeacon
                                                        status={d.status}
                                                    />
                                                </Box>
                                            )}
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {d.dealer}
                                                </Typography>
                                                {isBoats && d.model && (
                                                    <Typography
                                                        sx={{
                                                            color: 'text.secondary',
                                                            whiteSpace:
                                                                'nowrap',
                                                            fontSize: 10,
                                                            lineHeight: 1.2,
                                                            display: 'block',
                                                        }}
                                                    >
                                                        {d.model}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    {isBoats && (
                                        <TableCell sx={{ maxWidth: 150 }}>
                                            <BoatLocation
                                                city={d.city}
                                                state={d.state}
                                                country={d.country}
                                            />
                                        </TableCell>
                                    )}
                                    {!isBoats && (
                                        <TableCell align="right">
                                            {d.activeBoats.toLocaleString()}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Bar
                                            value={d.totalHours}
                                            max={maxTotal}
                                            color="#8BC441"
                                            deltaPct={d.totalHoursDeltaPct}
                                        />
                                    </TableCell>
                                    {showLastCol && (
                                    <TableCell>
                                        {isBoats ? (
                                            <Sparkline
                                                data={d.series || []}
                                                days={days}
                                                alarms={d.alarms}
                                                dtcs={d.dtcs}
                                                flipTip={i === 0}
                                                color="#508991"
                                                fmt={(v) =>
                                                    `${
                                                        Math.round(v * 10) / 10
                                                    } h`
                                                }
                                            />
                                        ) : (
                                            <Bar
                                                value={d.avgHours}
                                                max={maxAvg}
                                                color="#508991"
                                                deltaPct={d.avgHoursDeltaPct}
                                            />
                                        )}
                                    </TableCell>
                                    )}
                                </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </Box>
        </Paper>
    )
}

export default DealerPerformanceTable
